import { Button, Card, LinearProgress, Tab, Tabs } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMemo, useState } from 'react';
import { FaFileExcel, FaX } from 'react-icons/fa6';
import WarningsPopover from './articlesTablePreview/WarningsPopover';
import ArticlesTablePreview from './articlesTablePreview/ArticlesTablePreview';
import FormatsTablePreview from './articlesTablePreview/FormatsTablePreview';
import ProvidersFormatsTablePreview from './articlesTablePreview/ProvidersFormatsTablePreview';
import useCreateMassiveArticles from './articlesTablePreview/useCreateMassiveArticles';
import IssuesPopover from './articlesTablePreview/IssuesPopover';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
const getArticleKey = (a) => a.code?.toString() || a.name;
const getFormatKey = (f) => f.code?.toString();
const getFormatKeyT2 = (f) => f._excelRow?.toString();
const getProviderKey = (p) => p.code?.toString();

const makeToggle = (setter) => (key) =>
  setter((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

const makeToggleAll = (setter) => (keys, checked) =>
  setter((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => (checked ? next.add(k) : next.delete(k)));
    return next;
  });

const LoadedDataFormExcel = ({ dataExcel, setDataExcel, setFileName, fileName }) => {
  const [tabValue, setTabValue] = useState('resumen');
  const { data, mutateAsync, isLoading, progress } = useCreateMassiveArticles();
  const navigate = useNavigate();
  const isTemplate2 = dataExcel?.templateType === 'template-2';

  const [selectedArticleKeys, setSelectedArticleKeys] = useState(
    () =>
      new Set([
        ...(dataExcel?.newArticles ?? []).map(getArticleKey),
        ...(dataExcel?.editedArticles ?? []).map(getArticleKey),
        ...(dataExcel?.withOutChangesArticles ?? []).map(getArticleKey),
      ]),
  );
  const [selectedFormatKeys, setSelectedFormatKeys] = useState(() => {
    if (dataExcel?.templateType === 'template-2') {
      return new Set([
        ...(dataExcel?.newFormats ?? []).map(getFormatKeyT2),
        ...(dataExcel?.editedFormats ?? []).map(getFormatKeyT2),
      ]);
    }
    return new Set([
      ...(dataExcel?.newFormats ?? []).map(getFormatKey),
      ...(dataExcel?.editedFormats ?? []).map(getFormatKey),
    ]);
  });
  const [selectedProviderKeys, setSelectedProviderKeys] = useState(
    () =>
      new Set([
        ...(dataExcel?.newProvidersFormats ?? []).map(getProviderKey),
        ...(dataExcel?.editedProvidersFormats ?? []).map(getProviderKey),
        ...(dataExcel?.withOutChangesProvidersFormats ?? []).map(getProviderKey),
      ]),
  );

  const [markedDuplicates, setMarkedDuplicates] = useState({});

  const toggleArticle = makeToggle(setSelectedArticleKeys);
  const toggleAllArticles = makeToggleAll(setSelectedArticleKeys);
  const toggleFormat = makeToggle(setSelectedFormatKeys);
  const toggleAllFormats = makeToggleAll(setSelectedFormatKeys);
  const toggleProvider = makeToggle(setSelectedProviderKeys);
  const toggleAllProviders = makeToggleAll(setSelectedProviderKeys);

  const markAsDuplicate = (articleKey, similarCode) => {
    setMarkedDuplicates((prev) => {
      const already = prev[articleKey] === similarCode;
      const next = { ...prev };
      if (already) delete next[articleKey];
      else next[articleKey] = similarCode;
      return next;
    });
  };

  const closeModal = () => {
    navigate(`../`);
  };

  const isDisabled = useMemo(() => {
    let isDisabled = false;
    if (isLoading) isDisabled = true;
    if (data) isDisabled = true;

    if (isTemplate2) {
      if (
        dataExcel?.newArticles.length === 0 &&
        dataExcel?.editedArticles.length === 0 &&
        dataExcel?.newFormats.length === 0 &&
        dataExcel?.editedFormats.length === 0
      )
        isDisabled = true;
    } else {
      if (
        dataExcel?.newArticles.length === 0 &&
        dataExcel?.editedArticles.length === 0 &&
        dataExcel?.newFormats.length === 0 &&
        dataExcel?.editedFormats.length === 0 &&
        dataExcel?.newProvidersFormats.length === 0 &&
        dataExcel?.editedProvidersFormats.length === 0
      )
        isDisabled = true;
    }

    return isDisabled;
  }, [dataExcel, isLoading, data, isTemplate2]);

  if (isLoading) {
    return (
      <div className="loader-container-excel">
        <h4>Guardando datos...</h4>
        <div style={{ width: '100%', maxWidth: 480, height: '10rem', paddingTop: '4rem' }}>
          <LinearProgress
            color="error"
            variant="determinate"
            value={progress.pct}
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#555' }}>
            <span>{progress.message}</span>
            <span>{progress.pct}%</span>
          </div>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#888' }}>
          Este proceso puede tardar varios minutos según el tamaño del archivo.
        </div>
      </div>
    );
  }

  return (
    <div className="articles-excel-content">
      {/* <button onClick={() => console.log(dataExcel)}>CONSOLE LOG</button> */}
      <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)} aria-label="tabla-excel">
        <Tab className="tabs-navigate-admin" label="Resumen" value="resumen" />
        <Tab
          className="tabs-navigate-admin"
          value="articulos"
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              {isTemplate2 ? 'Articulos & Formatos' : 'Articulos'}
              {dataExcel?.withIssuesArticles?.length > 0 && (
                <ReportProblemOutlinedIcon style={{ color: '#c50202', width: '1rem', height: '1rem' }} />
              )}
              {dataExcel?.repeatArticleCode?.length > 0 && (
                <ReportProblemOutlinedIcon style={{ color: '#c50202', width: '1rem', height: '1rem' }} />
              )}
              {([...(dataExcel?.newArticles ?? []), ...(dataExcel?.editedArticles ?? [])].some(
                (a) => a.warnings?.length > 0,
              ) ||
                (isTemplate2 &&
                  [...(dataExcel?.newFormats ?? []), ...(dataExcel?.editedFormats ?? [])].some(
                    (f) => f.warnings?.length > 0,
                  ))) && <InfoOutlinedIcon style={{ color: '#e67e00', width: '1rem', height: '1rem' }} />}
            </span>
          }
        />
        {!isTemplate2 && (
          <Tab
            className="tabs-navigate-admin"
            value="formatos"
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                Formatos
                {dataExcel?.withIssuesFormats?.length > 0 && (
                  <ReportProblemOutlinedIcon style={{ color: '#c50202', width: '1rem', height: '1rem' }} />
                )}
                {[...(dataExcel?.newFormats ?? []), ...(dataExcel?.editedFormats ?? [])].some(
                  (f) => f.warnings?.length > 0,
                ) && <InfoOutlinedIcon style={{ color: '#e67e00', width: '1rem', height: '1rem' }} />}
              </span>
            }
          />
        )}
        {!isTemplate2 && (
          <Tab
            className="tabs-navigate-admin"
            value="proveedores"
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                Proveedores
                {dataExcel?.withIssuesProvidersFormats?.length > 0 && (
                  <ReportProblemOutlinedIcon style={{ color: '#c50202', width: '1rem', height: '1rem' }} />
                )}
                {[...(dataExcel?.newProvidersFormats ?? []), ...(dataExcel?.editedProvidersFormats ?? [])].some(
                  (p) => p.warnings?.length > 0,
                ) && <InfoOutlinedIcon style={{ color: '#e67e00', width: '1rem', height: '1rem' }} />}
              </span>
            }
          />
        )}
      </Tabs>

      {tabValue === 'resumen' && (
        <>
          <div>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<FaFileExcel />}
              endIcon={<FaX size="10" color="#cc0e17" />}
              disabled={isLoading}
              onClick={() => {
                setDataExcel(null);
                setFileName(null);
              }}
            >
              {fileName}
            </Button>
          </div>

          <p className="info">Se han obtenido los siguientes datos de excel proporcionado.</p>

          {dataExcel?.repeatArticleCode?.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
                padding: '0.75rem 1rem',
                background: '#fff3f3',
                border: '1px solid #c50202',
                borderRadius: '6px',
                marginBottom: '0.75rem',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#c50202', fontWeight: 600 }}>
                <ReportProblemOutlinedIcon style={{ width: '1rem', height: '1rem' }} />
                Códigos de artículo duplicados en el excel — estas filas se han ignorado:
              </span>
              {dataExcel.repeatArticleCode.map((r) => (
                <span key={r.code} style={{ fontSize: '0.85rem', color: '#c50202' }}>
                  • Código <strong>{r.code}</strong> aparece en filas{' '}
                  {r.articulos.map((a) => a.rowIndex + 2).join(', ')}
                </span>
              ))}
            </div>
          )}

          <div className="articles-excel-cards-container">
            <Card className="articles-excel-card">
              <h1 className={dataExcel.newArticles.length !== 0 ? 'created' : ''}>{dataExcel.newArticles.length}</h1>
              <span>nuevos articulos</span>
            </Card>
            <Card className="articles-excel-card">
              <h1 className={dataExcel.editedArticles.length !== 0 ? 'edited' : ''}>
                {dataExcel.editedArticles.length}
              </h1>
              <span>Articulos editados</span>
            </Card>
            <Card className="articles-excel-card">
              <h1>{dataExcel.withOutChangesArticles.length}</h1>
              <span>Articulos sin cambios</span>
            </Card>
            {isTemplate2 && (
              <>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.newFormats.length !== 0 ? 'created' : ''}>{dataExcel.newFormats.length}</h1>
                  <span>Nuevos formatos</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.editedFormats.length !== 0 ? 'edited' : ''}>
                    {dataExcel.editedFormats.length}
                  </h1>
                  <span>Formatos editados</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1>{dataExcel.withOutChangesFormats.length}</h1>
                  <span>Formatos sin cambios</span>
                </Card>
              </>
            )}
            {!isTemplate2 && (
              <>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.newFormats.length !== 0 ? 'created' : ''}>{dataExcel.newFormats.length}</h1>
                  <span>Nuevos formatos</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.editedFormats.length !== 0 ? 'edited' : ''}>
                    {dataExcel.editedFormats.length}
                  </h1>
                  <span>Formatos editados</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1>{dataExcel.withOutChangesFormats.length}</h1>
                  <span>Formatos sin cambios</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.newProvidersFormats.length !== 0 ? 'created' : ''}>
                    {dataExcel.newProvidersFormats.length}
                  </h1>
                  <span>Nuevos Proveedores</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1 className={dataExcel.editedProvidersFormats.length !== 0 ? 'edited' : ''}>
                    {dataExcel.editedProvidersFormats.length}
                  </h1>
                  <span>Proveedores editados</span>
                </Card>
                <Card className="articles-excel-card">
                  <h1>{dataExcel.withOutChangesProvidersFormats.length}</h1>
                  <span>Proveedores sin cambios</span>
                </Card>
              </>
            )}
          </div>
        </>
      )}

      {tabValue === 'articulos' && (
        <ArticlesTablePreview
          templateType={dataExcel?.templateType}
          newArticles={dataExcel?.newArticles ?? []}
          editedArticles={dataExcel?.editedArticles ?? []}
          withOutChangesArticles={dataExcel?.withOutChangesArticles ?? []}
          withIssuesArticles={dataExcel?.withIssuesArticles ?? []}
          newFormats={dataExcel?.newFormats ?? []}
          editedFormats={dataExcel?.editedFormats ?? []}
          withOutChangesFormats={dataExcel?.withOutChangesFormats ?? []}
          withIssuesFormats={dataExcel?.withIssuesFormats ?? []}
          selectedKeys={selectedArticleKeys}
          onToggle={toggleArticle}
          onToggleAll={toggleAllArticles}
          selectedFormatKeys={selectedFormatKeys}
          onToggleFormat={toggleFormat}
          onToggleAllFormats={toggleAllFormats}
          markedDuplicates={markedDuplicates}
          onMarkDuplicate={markAsDuplicate}
        />
      )}
      {tabValue === 'formatos' && (
        <FormatsTablePreview
          articles={[...dataExcel?.newArticles, ...dataExcel?.editedArticles, ...dataExcel?.withOutChangesArticles]}
          newFormats={dataExcel?.newFormats ?? []}
          editedFormats={dataExcel?.editedFormats ?? []}
          withOutChangesFormats={dataExcel?.withOutChangesFormats ?? []}
          withIssuesFormats={dataExcel?.withIssuesFormats ?? []}
          selectedKeys={selectedFormatKeys}
          onToggle={toggleFormat}
          onToggleAll={toggleAllFormats}
        />
      )}
      {tabValue === 'proveedores' && (
        <ProvidersFormatsTablePreview
          articles={[...dataExcel?.newArticles, ...dataExcel?.editedArticles, ...dataExcel?.withOutChangesArticles]}
          newProvidersFormats={dataExcel?.newProvidersFormats ?? []}
          editedProvidersFormats={dataExcel?.editedProvidersFormats ?? []}
          withOutChangesProvidersFormats={dataExcel?.withOutChangesProvidersFormats ?? []}
          withIssuesProvidersFormats={dataExcel?.withIssuesProvidersFormats ?? []}
          selectedKeys={selectedProviderKeys}
          onToggle={toggleProvider}
          onToggleAll={toggleAllProviders}
        />
      )}

      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', width: '100%', gap: '0.5rem' }}>
        {[
          ...dataExcel?.withIssuesArticles.flatMap((i) => i.issues),
          ...dataExcel?.withIssuesFormats.flatMap((i) => i.issues),
          ...dataExcel?.withIssuesProvidersFormats.flatMap((i) => i.issues),
        ].length > 0 && (
          <IssuesPopover
            issues={[
              ...dataExcel?.withIssuesArticles.flatMap((i) =>
                i.issues.map((issue) => ({
                  message: `[${i.excelSheet} - Fila ${i.excelRow}] ${issue?.message ?? issue}`,
                  field: issue?.field,
                })),
              ),
              ...dataExcel?.withIssuesFormats.flatMap((i) =>
                i.issues.map((issue) => ({
                  message: `[${i.excelSheet} - Fila ${i.excelRow}] ${issue?.message ?? issue}`,
                  field: issue?.field,
                })),
              ),
              ...dataExcel?.withIssuesProvidersFormats.flatMap((i) =>
                i.issues.map((issue) => ({
                  message: `[${i.excelSheet} - Fila ${i.excelRow}] ${issue?.message ?? issue}`,
                  field: issue?.field,
                })),
              ),
            ]}
          />
        )}
        {[
          ...(dataExcel?.newArticles ?? []),
          ...(dataExcel?.editedArticles ?? []),
          ...(dataExcel?.withOutChangesArticles ?? []),
          ...(dataExcel?.newFormats ?? []),
          ...(dataExcel?.editedFormats ?? []),
          ...(dataExcel?.newProvidersFormats ?? []),
          ...(dataExcel?.editedProvidersFormats ?? []),
        ].some((item) => item.warnings?.length > 0) && (
          <WarningsPopover
            warnings={[
              ...(dataExcel?.newArticles ?? []),
              ...(dataExcel?.editedArticles ?? []),
              ...(dataExcel?.withOutChangesArticles ?? []),
              ...(dataExcel?.newFormats ?? []),
              ...(dataExcel?.editedFormats ?? []),
              ...(dataExcel?.newProvidersFormats ?? []),
              ...(dataExcel?.editedProvidersFormats ?? []),
            ].flatMap((item) =>
              (item.warnings ?? []).map((w) => ({
                message: `[${w.excelSheet ?? item.excelSheet ?? ''} - Fila ${item._excelRow ?? item.excelRow ?? ''}] ${
                  w.message
                }`,
                field: w.field,
              })),
            )}
          />
        )}
      </div>
      <div
        style={{
          width: '100%',
          flex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2rem',
        }}
      >
        {!data ? (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                // eslint-disable-next-line no-restricted-globals
                if (confirm('¿Quieres salir del proceso de importación? Los cambios no guardados se perderán.')) {
                  navigate('../');
                }
              }}
            >
              Cancelar y salir
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={isDisabled}
              onClick={async () => {
                if (!dataExcel) return;

                await mutateAsync({
                  newArticles: (dataExcel?.newArticles ?? []).filter(
                    (a) => selectedArticleKeys.has(getArticleKey(a)) && !markedDuplicates[getArticleKey(a)],
                  ),
                  editedArticles: (dataExcel?.editedArticles ?? []).filter(
                    (a) => selectedArticleKeys.has(getArticleKey(a)) && !markedDuplicates[getArticleKey(a)],
                  ),

                  newFormats: isTemplate2
                    ? (dataExcel?.newFormats ?? []).filter((f) => selectedFormatKeys.has(getFormatKeyT2(f)))
                    : (dataExcel?.newFormats ?? []).filter((f) => selectedFormatKeys.has(getFormatKey(f))),
                  editedFormats: isTemplate2
                    ? (dataExcel?.editedFormats ?? []).filter((f) => selectedFormatKeys.has(getFormatKeyT2(f)))
                    : (dataExcel?.editedFormats ?? []).filter((f) => selectedFormatKeys.has(getFormatKey(f))),

                  newProvidersFormats: (dataExcel?.newProvidersFormats ?? []).filter((p) =>
                    selectedProviderKeys.has(getProviderKey(p)),
                  ),
                  editedProvidersFormats: (dataExcel?.editedProvidersFormats ?? []).filter((p) =>
                    selectedProviderKeys.has(getProviderKey(p)),
                  ),
                });
              }}
            >
              Confirmar y guardar
            </Button>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              flex: 1,
              gap: '0.5rem',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span style={{ display: 'flex', gap: '1rem', alignItems: 'center', color: '#0b991e' }}>
              Se han guardado los cambios con exito
              <CheckCircleIcon />
            </span>
            <Button variant="contained" onClick={() => closeModal()}>
              Cerrar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadedDataFormExcel;
